import { createClient, type SupabaseClient } from '@supabase/supabase-js';

let client: SupabaseClient | null = null;
let attempted = false;

/**
 * Null when the project is not configured, never a throw.
 *
 * `createClient` rejects an empty URL or key synchronously. Called from a
 * provider that wraps every route, that turned one missing build variable into
 * a blank page on the sale screen — a screen that needs no session at all.
 * A misconfiguration has to degrade, not erase the product.
 *
 * The anon key is public by design: it grants only what the policies grant, and
 * this app reads no table directly. The service role never reaches a browser.
 */
export function supabase(): SupabaseClient | null {
  if (attempted) {
    return client;
  }
  attempted = true;

  const url = import.meta.env.VITE_SUPABASE_URL;
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!url || !anonKey) {
    return null;
  }

  client = createClient(url, anonKey, {
    auth: {
      // The magic link comes back with the tokens in the URL fragment, and this
      // is what reads them before the router sees the route.
      detectSessionInUrl: true,
      persistSession: true,
      autoRefreshToken: true,
      // Implicit, because every link here is minted server-side by the admin
      // API and GoTrue answers those in the fragment. A PKCE client rejects
      // that URL outright — it has no code verifier, because it never started
      // the flow. See supabase.test.ts.
      flowType: 'implicit'
    }
  });
  return client;
}

/** Exposed for tests, which need a fresh decision per case. */
export function resetSupabaseForTests(): void {
  client = null;
  attempted = false;
}
